/**
 * ルートのエラー境界（#189 A、docs/76 §1.2 a・b）。画面の表示中の例外・遅れて読む部品（地図・PDF など）の読み込みの失敗で、
 * React がアプリ全体を外して真っ白になるのを止める。
 *   ・部品の読み込みの失敗（配備で前の版のファイルが消えた＝Failed to fetch dynamically imported module など）は、
 *     sessionStorage の印で1回だけ自動で再読み込み（新しい版を読む）。2回目も失敗したら止めの画面 h
 *   ・ただし、ほかのページへ移り始めたあとの失敗では読み直さない（2026-10-17、docs/76 §1.2 e）。Safari（WebKit）は移り始めた瞬間に
 *     読みかけの部品を止め（Mac・iPhone とも）、新しいページに替わるのはその後（新しいページの答えが届いたとき）。
 *     その間に読み直すと、利用者の移動を横取りして元のページに引き戻す
 *     （e2e の estimate-187 ⑤ が webkit だけで落ちた原因。Chrome は替わる瞬間まで読み続けるので起きない）。
 *     失敗したら何も描かずに待ち、そのあともこのページに居るときだけ読み直す（替われば待ちごと消える）。
 *     移り始めた合図は Navigation API の別の文書への navigate（リンク・location.*。Mac・iPhone の Safari と Chrome）。
 *     URL を打つ・ブックマークは合図が来ないので、合図が無くても少し待つ。beforeunload は常には付けない（離れるときの確かめと同じく、要る間だけ）
 *   ・それ以外の例外は止めの画面 h「画面を読み込み直してください」＋「再読込」
 * 置き場所は app.tsx（BrowserRouter の中、Routes の外）。新しい置き場所を足さない（画面ごとの境界は作らない）
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { CrashStop } from "./StopScreen.jsx";

/** 自動の再読み込みの印（この時刻から 60 秒のあいだは2回目をしない＝読み直しの繰り返しにしない） */
const RELOAD_KEY = "gb.chunkReload";
const RELOAD_GUARD_MS = 60_000;
/** 移り始めたあとの失敗：新しいページに替わるのを待つ上限。過ぎてもここに居れば、移動は取り消された（ダウンロード・確かめで残った） */
const LEAVE_WAIT_MS = 10_000;
/** 移る合図が無いときも少し待つ（URL を打つ・ブックマークで移ると合図が来ない。配備で消えた部品の読み直しはこの分だけ遅れる） */
const SETTLE_MS = 2_000;

/** このページから移り始めた時刻（0＝まだ） */
let leavingAt = 0;
type NavigateEventLike = Event & { destination?: { sameDocument?: boolean }; downloadRequest?: string | null };
if (typeof window !== "undefined") {
  // react-router の画面の移動（pushState）は同じ文書＝数えない。ダウンロードはページに残る＝数えない
  (window as { navigation?: EventTarget }).navigation?.addEventListener("navigate", (e) => {
    const n = e as NavigateEventLike;
    if (n.destination?.sameDocument === false && !n.downloadRequest) leavingAt = Date.now();
  });
}

/** 部品の読み込みが失敗したあと、このページに居続けるときだけ fn（移り始めていれば新しいページに替わるのを待つ。替わればこの待ちは消える） */
function whenStillHere(fn: () => void): void {
  window.setTimeout(fn, Date.now() - leavingAt < LEAVE_WAIT_MS ? LEAVE_WAIT_MS : SETTLE_MS);
}

/** 遅れて読む部品の読み込みの失敗か（Chrome・Firefox・Safari・Vite の文） */
export function isChunkLoadError(e: unknown): boolean {
  const msg = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload CSS|Loading chunk [\w-]+ failed/i.test(msg);
}

/** 1回だけ自動で再読み込みする（した＝true）。60 秒以内の2回目はしない */
export function reloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false; // 印を残せないブラウザでは自動で読み直さない（止めの画面の「再読込」）
  }
  window.location.reload();
  return true;
}

/** waiting＝部品の読み込みの失敗のあと、読み直すかを決めるまで・読み直している間（何も描かない） */
type State = { error: unknown; waiting: boolean };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, waiting: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error, waiting: isChunkLoadError(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // 問い合わせの「？」の足あと（lib/trail.ts）にも残るよう、console にも出す（入力した内容は出さない＝例外の文だけ）
    console.error("[画面の例外]", error instanceof Error ? `${error.name}: ${error.message}` : error, info.componentStack?.split("\n").slice(0, 4).join("\n"));
    if (!isChunkLoadError(error)) return;
    // 移り始めたあとの失敗なら、新しいページに替わってこの待ちごと消える。居続けたときだけ読み直す（2回目は止めの画面）
    whenStillHere(() => {
      if (!reloadOnce()) this.setState({ waiting: false });
    });
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    if (this.state.waiting) return null;
    return <CrashStop />;
  }
}
